import React, { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import api from '../../../lib/api';

export { AIOutput } from './AIOutput';
export { ChatInterface } from './ChatInterface';

const inputBaseClasses =
  'w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed';

// CostPerformanceSlider component - FIAS-specific slider for cost/performance tradeoff
export function CostPerformanceSlider({ id, props, style }: SDUIComponentProps) {
  const {
    label = 'Cost/Performance Balance',
    value: initialValue = 50,
    min = 0,
    max = 100,
    costLabel = 'Lower Cost',
    performanceLabel = 'Higher Performance',
    showValue = true,
    onChange,
    className = '',
  } = props as {
    label?: string;
    value?: number;
    min?: number;
    max?: number;
    costLabel?: string;
    performanceLabel?: string;
    showValue?: boolean;
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(initialValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(Number(e.target.value));
    if (onChange) onChange();
  };

  return (
    <div id={id} className={className} style={style as CSSProperties}>
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium text-foreground">{label}</label>
        {showValue && <span className="text-sm text-muted-foreground">{value}%</span>}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={handleChange}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
      />
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>{costLabel}</span>
        <span>{performanceLabel}</span>
      </div>
    </div>
  );
}

// EntityPicker component - FIAS-specific entity selection
export function EntityPicker({ id, props, style }: SDUIComponentProps) {
  const {
    label,
    value: initialValue = '',
    categoryId,
    required = false,
    disabled = false,
    placeholder = 'Select an entity...',
    onChange,
    className = '',
  } = props as {
    label?: string;
    value?: string;
    categoryId?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(initialValue);
  const [entities, setEntities] = useState<Array<{ entityId: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchEntities = async () => {
      setLoading(true);
      try {
        const result = await api.listEntities({ categoryId });
        setEntities(result.entities as Array<{ entityId: string; name: string }>);
      } catch (error) {
        console.error('Failed to fetch entities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEntities();
  }, [categoryId]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue(e.target.value);
    if (onChange) onChange();
  };

  return (
    <div id={id} className={className} style={style as CSSProperties}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled || loading}
        required={required}
        className={inputBaseClasses}
      >
        <option value="">{loading ? 'Loading...' : placeholder}</option>
        {entities.map((entity) => (
          <option key={entity.entityId} value={entity.entityId}>
            {entity.name}
          </option>
        ))}
      </select>
    </div>
  );
}

// CategoryPicker component - FIAS-specific category selection
export function CategoryPicker({ id, props, style }: SDUIComponentProps) {
  const {
    label,
    value: initialValue = '',
    required = false,
    disabled = false,
    placeholder = 'Select a category...',
    onChange,
    className = '',
  } = props as {
    label?: string;
    value?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(initialValue);
  const [categories, setCategories] = useState<Array<{ categoryId: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const result = await api.listCategories();
        setCategories(result.categories as Array<{ categoryId: string; name: string }>);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue(e.target.value);
    if (onChange) onChange();
  };

  return (
    <div id={id} className={className} style={style as CSSProperties}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled || loading}
        required={required}
        className={inputBaseClasses}
      >
        <option value="">{loading ? 'Loading...' : placeholder}</option>
        {categories.map((category) => (
          <option key={category.categoryId} value={category.categoryId}>
            {category.name}
          </option>
        ))}
      </select>
    </div>
  );
}
